import type { PerioChartResponse } from '@/common-components/composed/PerioChart/types';

import type { ImageAnalyses } from '../../../../@types/latestImagesV2';
import type {
  TClaimImage,
  TClaimNote,
  TClaimTooth,
  TDmfValue,
  TFlattenedTreatment,
  TNarrativeTooth,
} from '../../../../@types/rcm';
import type { TTreatment } from '../../../../react-query/useAppointments';

export type { PerioChartResponse };

export const TOOTH_LEVEL_FINDING_TYPES = [
  'caries',
  'restorative_adjacent_lucency',
];

export type TEnabledFindingTypes = {
  caries: boolean;
  ral: boolean;
  calculus: boolean;
  radiographic_boneloss: boolean;
  periapical_radiolucency: boolean;
};

export type TSelectTreatment = TTreatment & {
  disabled: boolean;
};

export type TClaimFindingsResponse = {
  images: ImageAnalyses[];
  claimImages: TClaimImage[];
  claimNotes: TClaimNote[];
  claimTeeth: TNarrativeTooth[];
  treatments: TFlattenedTreatment[];
  dmfValues: TDmfValue[];
  supportedTreatmentType: 'SRP' | null;
  defaultTeeth: TClaimTooth[];
  supportedTreatmentGroups: string[] | null;
};

export interface SlotTreatment {
  id: number;
  images: { id: number; score: number; teeth: TToothDisplay[] }[];
}
export interface SlotData {
  slot: number;
  treatments: SlotTreatment[];
}

export enum TreatmentGroupKey {
  SRP = 'SRP',
  CROWN = 'CROWN',
}

export type TTreatmentResponse = {
  txId: number;
  toothId: string | null;
  quadrant: 'LL' | 'LR' | 'UL' | 'UR' | null;
  txCode: string;
  txDescription: string;
  txGroupName: string;
  claimGroup: TreatmentGroupKey | null;
  note: string | null;
  warning: string | null;
};

export type SrpBoneLevel = {
  id: string;
  side: 'mesial' | 'distal';
};

export type TToothDisplay = {
  toothId: string;
  caries: string[] | null;
  boneLevelsV2: SrpBoneLevel[] | null;
  restorativeAdjacentLucency: string[] | null;
  dmf: number | null;
  calculus: string[] | null;
};

export type TTxDisplay = {
  txId: number;
  teeth: TToothDisplay[];
};

export type TImageDisplay = {
  id: number;
  sortKey: number;
  txs: TTxDisplay[];
};

export type ToothSurfaceValues = {
  distal: number | null;
  center: number | null;
  mesial: number | null;
};

export type GetClaimSummaryResponse = {
  images: ImageAnalyses[];
  treatments: TTreatmentResponse[];
  fmx: SlotData[] | null;
  imagesDisplay: TImageDisplay[] | null;
  perioChart: PerioChartResponse | null;
};

export type TToothTableData = TToothDisplay & {
  txCodes: Set<string>;
  toothId: string;
};

export type SrpTooth = {
  potentialCalculus: boolean;
  txCodes: string[];
  mesial: number | null;
  distal: number | null;
  toothId: string;
  measurementType: string | null;
  imageType: string | null;
};

export type SimplifiedTreatment = {
  txId: number;
  tooth: TToothDisplay;
};

export type ToothWithFindings = {
  toothId: string;
  findings: {
    caries: string[] | null;
    boneLevels: SrpBoneLevel[] | null;
    restorativeAdjacentLucency: string[] | null;
    dmf: number | null;
  };
};

// Types for batch-findings endpoint
export type TClaimImageWithAnalysis = ImageAnalyses;

export type TBatchFindingsRequest = {
  appointments: {
    unifiedAppointmentId: number;
    imagingPatientId: string;
  }[];
};

export type TBatchFindingsResponse = {
  images: TClaimImageWithAnalysis[];
  appointmentData: {
    unifiedAppointmentId: number;
    treatments: TTreatmentResponse[];
    fmx: SlotData[] | null;
    imagesDisplay: TImageDisplay[] | null;
    allTreatmentsExcluded?: boolean;
  }[];
};
